import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check } from 'lucide-react';

interface SelectOption {
  value: string;
  label: string;
  disabled: boolean;
}

interface OpenSelect {
  element: HTMLSelectElement;
  options: SelectOption[];
  selectedValue: string;
}

interface MenuPosition {
  left: number;
  top: number;
  width: number;
  maxHeight: number;
  opensUpward: boolean;
}

const FORM_SCOPE = '.tc-dashboard-shell, .tc-admin-shell, .tc-auth-page, .tc-auth-success';

const isEligibleSelect = (target: EventTarget | null): target is HTMLSelectElement => {
  if (!(target instanceof HTMLSelectElement)) return false;
  if (!target.closest(FORM_SCOPE)) return false;
  if (target.multiple || target.size > 1 || target.disabled) return false;
  return target.dataset.nativeSelect !== 'true';
};

const getOptions = (select: HTMLSelectElement): SelectOption[] => (
  Array.from(select.options).map((option) => ({
    value: option.value,
    label: option.label || option.textContent || option.value,
    disabled: option.disabled || Boolean(option.parentElement?.closest('optgroup[disabled]')),
  }))
);

const setNativeValue = (select: HTMLSelectElement, value: string) => {
  const setter = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value')?.set;
  setter?.call(select, value);
  select.dispatchEvent(new Event('input', { bubbles: true }));
  select.dispatchEvent(new Event('change', { bubbles: true }));
};

const QuietOutlineSelectBridge: React.FC = () => {
  const [openSelect, setOpenSelect] = useState<OpenSelect | null>(null);
  const [position, setPosition] = useState<MenuPosition | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const close = () => setOpenSelect(null);

  useEffect(() => {
    const open = (select: HTMLSelectElement) => {
      setOpenSelect((current) => {
        if (current?.element === select) return null;
        return {
          element: select,
          options: getOptions(select),
          selectedValue: select.value,
        };
      });
    };

    const onPointerDown = (event: PointerEvent) => {
      if (!isEligibleSelect(event.target)) return;
      event.preventDefault();
      open(event.target);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close();
        return;
      }
      if (!isEligibleSelect(event.target)) return;
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        open(event.target);
      }
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, []);

  useLayoutEffect(() => {
    if (!openSelect) {
      setPosition(null);
      return;
    }

    const select = openSelect.element;
    if (!select.isConnected) {
      close();
      return;
    }

    select.classList.add('tc-native-select-open');
    const updatePosition = () => {
      const rect = select.getBoundingClientRect();
      const viewportPadding = 10;
      const gap = 6;
      const desiredHeight = Math.min(openSelect.options.length * 42 + 12, 280);
      const spaceBelow = window.innerHeight - rect.bottom - viewportPadding - gap;
      const spaceAbove = rect.top - viewportPadding - gap;
      const opensUpward = spaceBelow < Math.min(desiredHeight, 180) && spaceAbove > spaceBelow;
      const availableHeight = Math.max(96, opensUpward ? spaceAbove : spaceBelow);
      const maxHeight = Math.min(desiredHeight, availableHeight);
      const width = Math.min(Math.max(rect.width, 180), window.innerWidth - viewportPadding * 2);
      const left = Math.min(
        Math.max(viewportPadding, rect.left),
        window.innerWidth - width - viewportPadding,
      );
      const top = opensUpward
        ? Math.max(viewportPadding, rect.top - gap - maxHeight)
        : Math.min(window.innerHeight - maxHeight - viewportPadding, rect.bottom + gap);
      setPosition({ left, top, width, maxHeight, opensUpward });
    };

    const onOutsidePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (target === select || menuRef.current?.contains(target)) return;
      close();
    };
    const onViewportChange = () => close();

    updatePosition();
    document.addEventListener('pointerdown', onOutsidePointerDown);
    window.addEventListener('resize', onViewportChange);
    window.addEventListener('scroll', onViewportChange, true);
    return () => {
      select.classList.remove('tc-native-select-open');
      document.removeEventListener('pointerdown', onOutsidePointerDown);
      window.removeEventListener('resize', onViewportChange);
      window.removeEventListener('scroll', onViewportChange, true);
    };
  }, [openSelect]);

  useEffect(() => {
    if (!position || !menuRef.current) return;
    const selected = menuRef.current.querySelector<HTMLElement>('[aria-selected="true"]');
    selected?.scrollIntoView({ block: 'nearest' });
    selected?.focus({ preventScroll: true });
  }, [position]);

  if (!openSelect || !position) return null;

  return createPortal(
    <div
      ref={menuRef}
      role="listbox"
      aria-label={openSelect.element.getAttribute('aria-label') || 'Select an option'}
      className={`tc-native-select-menu ${position.opensUpward ? 'is-upward' : ''}`}
      style={{
        left: position.left,
        top: position.top,
        width: position.width,
        maxHeight: position.maxHeight,
      }}
    >
      {openSelect.options.map((option, index) => {
        const selected = option.value === openSelect.selectedValue;
        return (
          <button
            key={`${option.value}-${index}`}
            type="button"
            role="option"
            aria-selected={selected}
            disabled={option.disabled}
            className={`tc-native-select-menu__option ${selected ? 'is-selected' : ''}`}
            onClick={() => {
              setNativeValue(openSelect.element, option.value);
              openSelect.element.focus({ preventScroll: true });
              close();
            }}
          >
            <span>{option.label}</span>
            {selected && <Check size={15} strokeWidth={2.8} aria-hidden="true" />}
          </button>
        );
      })}
    </div>,
    document.body,
  );
};

export default QuietOutlineSelectBridge;
